import React, { useState, useCallback } from 'react';
import { Upload, message, Typography, Card, Modal, Form } from 'antd';
import { CloudUploadOutlined } from '@ant-design/icons';
import axios from 'axios';
import UploadStep from './UploadStep';
import SchemaMapping from './SchemaMapping';

const { Title } = Typography;

const DataSourceUpload = ({ onUploadComplete, onUploadProgress, onCancel, editingConnection = null, editingSchema = null, droppedFile = null }) => {
  // Helper function to handle authentication errors
  const handleAuthError = (error) => {
    if (error.response?.status === 401 || error.response?.status === 204 || 
        (error.response?.status === 400)) {
      localStorage.removeItem('auth-token');
      message.error('Session expired. Please log in again.');
      window.location.href = '/login';
      return true;
    }
    return false;
  };

  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [connectionName, setConnectionName] = useState(editingConnection?.name || editingSchema?.name || '');
  const [description, setDescription] = useState(editingConnection?.description || editingSchema?.description || '');
  const [skipRows, setSkipRows] = useState(editingSchema?.dataStructure?.skipRows || 0);
  const [currentStep, setCurrentStep] = useState(editingSchema ? 2 : 1); // Start at step 2 for schema editing
  const [form] = Form.useForm();
  const [dragActive, setDragActive] = useState(false);
  const [nameValidationStatus, setNameValidationStatus] = useState('');
  const [nameValidationMessage, setNameValidationMessage] = useState('');
  const [isCheckingName, setIsCheckingName] = useState(false);
  const [nameValidationCache, setNameValidationCache] = useState({});
  const [fileSchema, setFileSchema] = useState(null);
  const [loadingSchema, setLoadingSchema] = useState(false);

  // Cleanup temporary file on component unmount or cancel
  const cleanupTemporaryFile = async (fileInfo) => {
    if (fileInfo?.status === 'temporary' && fileInfo?.s3Key && fileInfo?.s3Bucket) {
      try {
        await axios.post('/workflow/cleanup-temporary-file', {
          s3Key: fileInfo.s3Key,
          s3Bucket: fileInfo.s3Bucket
        }, {
          headers: {
            'auth-token': localStorage.getItem('auth-token')
          }
        });
      } catch (error) {
        console.error('Error cleaning up temporary file:', error);
      }
    }
  };

  // Cleanup on component unmount
  React.useEffect(() => {
    return () => {
      if (uploadedFile?.status === 'temporary') {
        cleanupTemporaryFile(uploadedFile);
      }
    };
  }, []);

  const handleCancel = () => {
    // Cleanup temporary file before canceling
    if (uploadedFile?.status === 'temporary') {
      cleanupTemporaryFile(uploadedFile);
    }
    onCancel();
  };

  const handleNext = async () => {
    try {
      // For new connections, require file upload (but not for schema editing)
      if (!editingConnection && !editingSchema && !uploadedFile) {
        message.error('Please upload a file first');
        return;
      }

      // Auto-fill name from uploaded file if empty
      let finalConnectionName = connectionName.trim();
      if (!finalConnectionName && uploadedFile?.originalName) {
        finalConnectionName = uploadedFile.originalName.replace(/\.[^/.]+$/, "");
        setConnectionName(finalConnectionName);
        form.setFieldValue('name', finalConnectionName);
      }

      // Enforce name requirement
      if (!finalConnectionName) {
        message.error('Please enter a connection name');
        form.scrollToField('name');
        return;
      }

      // Validate form fields
      await form.validateFields();

      // Check for name conflicts before proceeding
      if (nameValidationStatus === 'error') {
        message.error('Connection name must be unique. Please choose a different name.');
        form.scrollToField('name');
        return;
      }

      // Fetch file schema
      setLoadingSchema(true);
      const response = await axios.post('/workflow/extract-schema', {
        fileInfo: uploadedFile,
        skipRows: skipRows
      }, {
        headers: {
          'auth-token': localStorage.getItem('auth-token')
        }
      });

      if (response.data.success) {
        setFileSchema(response.data.data);
        setCurrentStep(2);
      } else {
        message.error(response.data.error || 'Failed to extract file schema');
      }
    } catch (error) {
      if (handleAuthError(error)) return;
      console.error('Error proceeding to next step:', error);
      message.error('Failed to process file. Please try again.');
    } finally {
      setLoadingSchema(false);
    }
  };

  // Initialize form with editing data
  React.useEffect(() => {
    console.log('editingConnection || editingSchema: ', editingConnection, editingSchema)
    const connectionData = editingConnection || editingSchema;
    if (connectionData) {
      form.setFieldsValue({
        name: connectionData.name,
        description: connectionData.description
      });
      setConnectionName(connectionData.name);
      setDescription(connectionData.description);
      // Set uploaded file info for editing mode
      setUploadedFile({
        originalName: connectionData.fileInfo?.originalName,
        fileSize: connectionData.fileInfo?.fileSize,
        fileType: connectionData.fileType,
        uploadedAt: connectionData.fileInfo?.uploadedAt,
        s3Key: connectionData.fileInfo?.s3Key,
        s3Bucket: connectionData.fileInfo?.s3Bucket,
        status: 'ready' // Mark as ready since it's an existing file
      });
      
      // For schema editing, use the saved schema from database
      if (editingSchema && connectionData) {
        console.log('Loading saved schema for editing connection:', connectionData.name);
        console.log('Data structures:', connectionData.dataStructures);
        console.log('Legacy data structure:', connectionData.dataStructure);
        
        // Use dataStructures array for multi-sheet support, fallback to legacy dataStructure
        const dataStructures = connectionData.dataStructures || (connectionData.dataStructure ? [connectionData.dataStructure] : []);
        
        if (dataStructures.length > 0) {
          // Build sheets array from all data structures
          const sheets = dataStructures.map((ds, index) => ({
            name: ds.sourceIdentifier || `Sheet${index + 1}`,
            rowCount: ds.rowCount || 0,
            columnCount: ds.columns?.length || 0,
            index: index
          }));
          
          // Find the first configured sheet or use the first sheet
          const activeDataStructure = dataStructures.find(ds => ds.isConfigured) || dataStructures[0];
          const activeSheetName = activeDataStructure?.sourceIdentifier || sheets[0]?.name;
          
          // Build allSheetSchemas object
          const allSheetSchemas = {};
          dataStructures.forEach(ds => {
            const sheetName = ds.sourceIdentifier;
            if (sheetName) {
              allSheetSchemas[sheetName] = {
                columns: (ds.columns || []).map((col, index) => ({
                  index,
                  position: col.position || String.fromCharCode(65 + index),
                  name: col.name || `Column ${index + 1}`,
                  dataType: col.type || 'Text',
                  sampleValues: [] // No sample values for saved schema
                })),
                totalRows: ds.rowCount || 0,
                columnCount: ds.columns?.length || 0
              };
            }
          });
          
          // Use saved database schema with multi-sheet support
          const savedSchema = {
            columns: (activeDataStructure?.columns || []).map((col, index) => ({
              index,
              position: col.position || String.fromCharCode(65 + index),
              name: col.name || `Column ${index + 1}`,
              dataType: col.type || 'Text',
              sampleValues: [] // No sample values for saved schema
            })),
            sheets: sheets,
            skipRows: activeDataStructure?.skipRows || 0,
            totalRows: activeDataStructure?.rowCount || 0,
            activeSheet: activeSheetName,
            fileName: connectionData.fileInfo?.originalName,
            allSheetSchemas: allSheetSchemas // Include all sheet schemas
          };
          
          console.log('Setting saved schema for editing with multi-sheet support:', savedSchema);
          setFileSchema(savedSchema);
        } else {
          console.warn('No data structures found for editing schema');
        }
      }
    }
  }, [editingConnection, editingSchema, form]);

  // Handle dropped file from workflow diagram
  React.useEffect(() => {
    if (droppedFile && !editingConnection && !editingSchema) {
      // Auto-start upload for dropped file
      uploadFileInChunks(droppedFile);
    }
  }, [droppedFile, editingConnection, editingSchema]);

  const CHUNK_SIZE = 10 * 1024 * 1024; // 10MB chunks for faster uploads and better timeout handling
  const ALLOWED_EXTENSIONS = ['.xlsx', '.xls', '.csv'];
  const ALLOWED_MIME_TYPES = [
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel',
    'text/csv',
    'application/csv'
  ];

  // Check if connection name is unique with caching
  const checkNameUniqueness = async (name) => {
    if (!name.trim()) return;
    
    const trimmedName = name.trim().toLowerCase();
    const cacheKey = `${trimmedName}_${editingConnection?.id || editingSchema?.id || 'new'}`;
    
    // Check cache first
    if (nameValidationCache[cacheKey]) {
      const cached = nameValidationCache[cacheKey];
      setNameValidationStatus(cached.status);
      setNameValidationMessage(cached.message);
      return;
    }
    
    setIsCheckingName(true);
    try {
      const response = await axios.post('/workflow/connections', {transformer: false}, {
        headers: {
          'auth-token': localStorage.getItem('auth-token')
        }
      });
      
      if (response.data.success) {
        const existingConnection = response.data.data.find(conn => 
          conn.name.toLowerCase() === trimmedName && 
          (!(editingConnection || editingSchema) || conn.id !== (editingConnection?.id || editingSchema?.id))
        );

        const result = existingConnection ? {
          status: 'error',
          message: 'Connection name must be unique. Please choose a different name.'
        } : {
          status: 'success',
          message: 'Name is available'
        };

        // Cache the result
        setNameValidationCache(prev => ({
          ...prev,
          [cacheKey]: result
        }));
        
        setNameValidationStatus(result.status);
        setNameValidationMessage(result.message);
      }
    } catch (error) {
      if (handleAuthError(error)) return;
      console.error('Error checking name uniqueness:', error);
      setNameValidationStatus('');
      setNameValidationMessage('');
    } finally {
      setIsCheckingName(false);
    }
  };

  // Debounced name validation with longer delay to reduce requests
  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (connectionName.trim()) {
        checkNameUniqueness(connectionName);
      } else {
        setNameValidationStatus('');
        setNameValidationMessage('');
      }
    }, 1000); // Increased from 500ms to 1000ms to reduce API calls

    return () => clearTimeout(timeoutId);
  }, [connectionName, editingConnection]);

  const validateFile = (file) => {
    const fileExtension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
    
    if (!ALLOWED_EXTENSIONS.includes(fileExtension)) {
      message.error('Only Excel (.xlsx, .xls) and CSV (.csv) files are allowed!');
      return false;
    }

    if (!ALLOWED_MIME_TYPES.includes(file.type)) {
      message.error('Invalid file format. Please upload a valid Excel or CSV file.');
      return false;
    }

    if (file.size > 50 * 1024 * 1024 * 1024) { // 50GB limit
      message.error('File size must be less than 50GB!');
      return false;
    }

    return true;
  };


  const uploadChunkWithRetry = async (formData, maxRetries = 3) => {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const response = await axios.post('/workflow/upload-chunk', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'auth-token': localStorage.getItem('auth-token')
          },
          timeout: 120000 // 2 minutes timeout for chunk uploads to handle slower connections
        });

        if (!response.data.success) {
          throw new Error(response.data.error || 'Upload failed');
        }

        return response;
      } catch (error) {
        console.log(`Chunk upload attempt ${attempt} failed:`, error.message);
        
        if (attempt === maxRetries) {
          throw error;
        }
        
        // Wait before retrying (exponential backoff)
        await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
      }
    }
  };

  const uploadFileInChunks = async (file) => {
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    const uploadId = Date.now().toString() + Math.random().toString(36).substr(2, 9);
    
    try {
      setUploading(true);
      setUploadProgress(0);

      for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
        const start = chunkIndex * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunk = file.slice(start, end);

        const formData = new FormData();
        formData.append('chunk', chunk);
        formData.append('chunkIndex', chunkIndex);
        formData.append('totalChunks', totalChunks);
        formData.append('uploadId', uploadId);
        formData.append('originalName', file.name);
        formData.append('fileSize', file.size);

        await uploadChunkWithRetry(formData);

        const progress = Math.round(((chunkIndex + 1) / totalChunks) * 100);
        setUploadProgress(progress);
        
        if (onUploadProgress) {
          onUploadProgress(progress);
        }
      }

      // Finalize upload
      const finalizeResponse = await axios.post('/workflow/finalize-upload', {
        uploadId,
        totalChunks,
        originalName: file.name,
        fileSize: file.size,
        connectionName: connectionName || file.name.replace(/\.[^/.]+$/, ""),
        description: description || `Data source from ${file.name}`
      }, {
        headers: {
          'auth-token': localStorage.getItem('auth-token')
        },
        timeout: 60000 // 1 minute timeout for finalization
      });

      if (finalizeResponse.data.success) {
        const uploadResult = finalizeResponse.data.data;
        setUploadedFile(uploadResult);
        
        // Auto-fill connection name if empty
        if (!connectionName.trim() && uploadResult.originalName) {
          const autoName = uploadResult.originalName.replace(/\.[^/.]+$/, ""); // Remove file extension
          setConnectionName(autoName);
          form.setFieldValue('name', autoName);
          
          // Check uniqueness of auto-filled name
          setTimeout(() => {
            checkNameUniqueness(autoName);
          }, 100);
        }
        
        message.success('File uploaded successfully! Please enter connection details and save.');
      } else {
        throw new Error(finalizeResponse.data.error || 'Failed to finalize upload');
      }

    } catch (error) {
      if (handleAuthError(error)) return;
      console.error('Upload error:', error);
      message.error(error.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      if (validateFile(file)) {
        if (editingConnection) {
          // In edit mode, confirm file overwrite
          Modal.confirm({
            title: 'Replace File',
            content: `Are you sure you want to replace the existing file "${editingConnection.fileInfo?.originalName}" with "${file.name}"?`,
            okText: 'Replace',
            okType: 'primary',
            cancelText: 'Cancel',
            onOk: () => {
              uploadFileInChunks(file);
            }
          });
        } else {
          uploadFileInChunks(file);
        }
      }
    }
  }, [editingConnection]);

  // Full-page drag and drop handlers
  const handlePageDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  }, []);

  const handlePageDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget.contains(e.relatedTarget)) {
      return;
    }
    setDragActive(false);
  }, []);

  const handlePageDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handlePageDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      const file = files[0];
      if (validateFile(file)) {
        if (editingConnection) {
          // In edit mode, confirm file overwrite
          Modal.confirm({
            title: 'Replace File',
            content: `Are you sure you want to replace the existing file "${editingConnection.fileInfo?.originalName}" with "${file.name}"?`,
            okText: 'Replace',
            okType: 'primary',
            cancelText: 'Cancel',
            onOk: () => {
              uploadFileInChunks(file);
            }
          });
        } else {
          uploadFileInChunks(file);
        }
      }
    }
  }, [editingConnection]);

  // Add event listeners for full-page drag and drop
  React.useEffect(() => {
    const container = document.querySelector('.ant-modal-body');
    if (container) {
      container.addEventListener('dragenter', handlePageDragEnter);
      container.addEventListener('dragleave', handlePageDragLeave);
      container.addEventListener('dragover', handlePageDragOver);
      container.addEventListener('drop', handlePageDrop);
      
      return () => {
        container.removeEventListener('dragenter', handlePageDragEnter);
        container.removeEventListener('dragleave', handlePageDragLeave);
        container.removeEventListener('dragover', handlePageDragOver);
        container.removeEventListener('drop', handlePageDrop);
      };
    }
  }, [handlePageDragEnter, handlePageDragLeave, handlePageDragOver, handlePageDrop]);

  const beforeUpload = (file) => {
    if (validateFile(file)) {
      if (editingConnection) {
        // In edit mode, confirm file overwrite
        Modal.confirm({
          title: 'Replace File',
          content: `Are you sure you want to replace the existing file "${editingConnection.fileInfo?.originalName}" with "${file.name}"?`,
          okText: 'Replace',
          okType: 'primary',
          cancelText: 'Cancel',
          onOk: () => {
            uploadFileInChunks(file);
          }
        });
      } else {
        uploadFileInChunks(file);
      }
    }
    return false; // Prevent default upload behavior
  };

  const uploadProps = {
    name: 'file',
    multiple: false,
    accept: '.xlsx,.xls,.csv',
    beforeUpload,
    onDrop: handleDrop,
    showUploadList: false,
    disabled: uploading,
  };

  const handleCreateConnection = async () => {
    try {
      // For new connections, require file upload
      if (!editingConnection && !uploadedFile) {
        message.error('Please upload a file first');
        return;
      }

      // Auto-fill name from uploaded file if empty
      let finalConnectionName = connectionName.trim();
      if (!finalConnectionName && uploadedFile?.originalName) {
        finalConnectionName = uploadedFile.originalName.replace(/\.[^/.]+$/, "");
        setConnectionName(finalConnectionName);
        form.setFieldValue('name', finalConnectionName);
        
        // Check uniqueness of auto-filled name immediately
        await new Promise(resolve => {
          checkNameUniqueness(finalConnectionName).then(resolve);
        });
      }

      // Enforce name requirement
      if (!finalConnectionName) {
        message.error('Please enter a connection name');
        form.scrollToField('name');
        return;
      }

      // Validate form fields
      await form.validateFields();

      // Check for name conflicts before proceeding
      if (nameValidationStatus === 'error') {
        message.error('Connection name must be unique. Please choose a different name.');
        form.scrollToField('name');
        return;
      }

      if (editingConnection || editingSchema) {
        console.log('editingSchema: ', editingSchema)
        // Update existing connection
        const connectionId = editingConnection?._id || editingSchema?._id;
        let updateData = {
          action: 'update',
          connectionId: connectionId,
          name: finalConnectionName,
          description: description
        };
        
        // If editing schema, use the dedicated schema update endpoint
        if (editingSchema) {
          const schemaResponse = await axios.post('/workflow/update-connection-schema', {
            connectionId: connectionId,
            schemaMapping: fileSchema
          }, {
            headers: {
              'Content-Type': 'application/json',
              'auth-token': localStorage.getItem('auth-token')
            }
          });

          if (schemaResponse.data.success) {
            message.success('Schema updated successfully');
            if (onUploadComplete) {
              onUploadComplete(schemaResponse.data.data);
            }
            return; // Exit early, schema update is complete
          } else {
            message.error(schemaResponse.data.error || 'Failed to update schema');
            return;
          }
        }

        // If a new file was uploaded, include the file information
        if (uploadedFile) {
          updateData.fileInfo = {
            originalName: uploadedFile.originalName,
            fileName: uploadedFile.filename,
            fileSize: uploadedFile.fileSize,
            s3Key: uploadedFile.s3Key,
            s3Bucket: uploadedFile.s3Bucket,
            etag: uploadedFile.etag,
            uploadedAt: new Date().toISOString()
          };
          updateData.fileType = uploadedFile.fileType;
        }

        const response = await axios.post('/workflow/connection-action', updateData, {
          headers: {
            'Content-Type': 'application/json',
            'auth-token': localStorage.getItem('auth-token')
          }
        });

        if (response.data.success) {
          message.success(uploadedFile ? 'Data source and file updated successfully' : 'Data source updated successfully');
          if (onUploadComplete) {
            onUploadComplete(response.data.data);
          }
        } else {
          message.error(response.data.error || 'Failed to update data source');
        }
      } else {
        // Create new connection from temporary file with schema mapping
        const response = await axios.post('/workflow/create-connection', {
          name: finalConnectionName,
          description: description,
          fileInfo: uploadedFile,
          skipRows: skipRows,
          schemaMapping: fileSchema
        }, {
          headers: {
            'auth-token': localStorage.getItem('auth-token')
          }
        });

        const result = response.data;
        if (result.success) {
          message.success('Data source created successfully');
          if (onUploadComplete) {
            onUploadComplete(result.data);
          }
        } else {
          message.error(result.error || 'Failed to create data source');
        }
      }
    } catch (error) {
      if (handleAuthError(error)) return;
      console.error('Error saving connection:', error);
      message.error('Failed to save data source');
    }
  };



  return (
    <Card style={{ position: 'relative' }}>
      {dragActive && currentStep === 1 && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(24, 144, 255, 0.1)',
          border: '2px dashed #1890ff',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '6px'
        }}>
          <div style={{ textAlign: 'center' }}>
            <CloudUploadOutlined style={{ fontSize: 48, color: '#1890ff', marginBottom: 8 }} />
            <Title level={4} style={{ color: '#1890ff', margin: 0 }}>Drop your file here</Title>
          </div>
        </div>
      )}
      
      {currentStep === 1 ? (
        <UploadStep
          editingConnection={editingConnection}
          droppedFile={droppedFile}
          uploading={uploading}
          uploadProgress={uploadProgress}
          uploadedFile={uploadedFile}
          connectionName={connectionName}
          setConnectionName={setConnectionName}
          description={description}
          setDescription={setDescription}
          skipRows={skipRows}
          setSkipRows={setSkipRows}
          form={form}
          nameValidationStatus={nameValidationStatus}
          nameValidationMessage={nameValidationMessage}
          isCheckingName={isCheckingName}
          loadingSchema={loadingSchema}
          handleCancel={handleCancel}
          handleNext={handleNext}
          handleCreateConnection={handleCreateConnection}
          uploadProps={uploadProps}
        />
      ) : (
        <SchemaMapping
          editingSchema={editingSchema}
          uploading={uploading}
          loadingSchema={loadingSchema}
          setLoadingSchema={setLoadingSchema}
          fileSchema={fileSchema}
          setFileSchema={setFileSchema}
          uploadedFile={uploadedFile}
          skipRows={skipRows}
          handleCancel={handleCancel}
          handleCreateConnection={handleCreateConnection}
          setCurrentStep={setCurrentStep}
        />
      )}
    </Card>
  );
};

export default DataSourceUpload;